import { defineComponent as _defineComponent } from 'vue';
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, createVNode as _createVNode, withCtx as _withCtx, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers } from "vue";
const _hoisted_1 = ["disabled"];
import { computed, nextTick, onMounted, ref } from 'vue';
import { PinInputInput, PinInputRoot } from 'reka-ui';
import { LoginAPI } from '@/api/login';
import { sendMetrics } from '@/utils/metrics';
import { trlMessage } from '@/services/i18n';
import { UiButton, UiError } from '@huntflow/ui';
import { useInterval, useLocalStorage, useWindowSize } from '@vueuse/core';
const BAD_REQUEST_ERROR = 'bad_request_error';
const TOO_MANY_REQUESTS_ERROR = 'too_many_requests_error';
const PIN_RETRY_UNTIL_STORAGE_KEY = 'otpValidationRetryUntil';
export default /*@__PURE__*/ _defineComponent({
    __name: 'phone-code',
    props: {
        phone: {},
        uuid: {},
        retryCount: { default: 0 },
        retryLoading: { type: Boolean, default: false }
    },
    emits: ["complete", "back", "retry"],
    setup(__props, { emit: __emit }) {
        const emit = __emit;
        const pinContainer = ref(null);
        const codeValue = ref([]);
        const errorMessage = ref('');
        const loading = ref(false);
        const nextSendPinSeconds = ref(0);
        const retryPinUntil = useLocalStorage(PIN_RETRY_UNTIL_STORAGE_KEY, 0);
        const { pause: pauseRetryPinTimer, resume: resumeRetryPinTimer } = useInterval(1000, {
            controls: true,
            immediate: false,
            callback: updateRetrySendPinSeconds
        });
        const retryLabel = computed(() => __props.retryCount > 0
            ? trlMessage('login.code.retry.timer', { count: formatTimer(__props.retryCount) })
            : trlMessage('login.code.retry'));
        const retryPinError = computed(() => nextSendPinSeconds.value > 0
            ? trlMessage('error.code.too_many_attempts', {
                count: formatTimer(nextSendPinSeconds.value)
            })
            : '');
        const displayedError = computed(() => retryPinError.value || errorMessage.value);
        const { width } = useWindowSize();
        const buttonSize = computed(() => (width.value < 756 ? 'xl' : 'xxl'));
        onMounted(() => {
            if (!restoreRetrySendPinCountdown()) {
                focusCode();
            }
        });
        function focusCode() {
            nextTick(() => pinContainer.value?.querySelector('input:not([type="hidden"])')?.focus());
        }
        function onSubmit() {
            if (loading.value || nextSendPinSeconds.value > 0) {
                return;
            }
            if (!__props.uuid) {
                errorMessage.value = trlMessage('error.send.code');
                emit('back');
                return;
            }
            if (codeValue.value.length !== 6) {
                errorMessage.value = trlMessage('error.code.length');
                focusCode();
                return;
            }
            loading.value = true;
            sendMetrics('enter');
            LoginAPI.authUser({
                code: codeValue.value.join(''),
                uuid: __props.uuid
            })
                .then((resp) => {
                clearRetrySendPinCountdown();
                emit('complete', resp);
            })
                .catch((error) => {
                const apiError = error.response?.data?.errors?.[0];
                const nextTryAfter = Number(apiError?.detail?.next_try_after || 0);
                if (nextTryAfter > 0) {
                    startRetrySendPinCountdown(nextTryAfter);
                }
                if (nextTryAfter > 0) {
                    errorMessage.value = '';
                }
                else if (apiError?.type === BAD_REQUEST_ERROR) {
                    errorMessage.value = trlMessage('error.BAD_CODE');
                }
                else if (apiError?.type === TOO_MANY_REQUESTS_ERROR) {
                    errorMessage.value = '';
                }
                else {
                    errorMessage.value = trlMessage('error.send.code');
                }
                codeValue.value = [];
                if (!nextTryAfter) {
                    focusCode();
                }
            })
                .finally(() => {
                loading.value = false;
            });
        }
        function startRetrySendPinCountdown(seconds) {
            const retryUntil = Date.now() + Math.max(0, Math.ceil(seconds)) * 1000;
            retryPinUntil.value = retryUntil;
            runRetrySendPinCountdown(retryUntil);
        }
        function restoreRetrySendPinCountdown() {
            const retryUntil = retryPinUntil.value;
            if (!retryUntil || retryUntil <= Date.now()) {
                retryPinUntil.value = null;
                return false;
            }
            runRetrySendPinCountdown(retryUntil);
            return true;
        }
        function runRetrySendPinCountdown(retryUntil) {
            pauseRetryPinTimer();
            retryPinUntil.value = retryUntil;
            updateRetrySendPinSeconds();
            if (nextSendPinSeconds.value === 0) {
                clearRetrySendPinCountdown();
                return;
            }
            resumeRetryPinTimer();
        }
        function updateRetrySendPinSeconds() {
            nextSendPinSeconds.value = Math.max(0, Math.ceil((retryPinUntil.value - Date.now()) / 1000));
            if (nextSendPinSeconds.value === 0) {
                clearRetrySendPinCountdown();
                focusCode();
            }
        }
        function clearRetrySendPinCountdown() {
            pauseRetryPinTimer();
            nextSendPinSeconds.value = 0;
            retryPinUntil.value = null;
        }
        function onComplete(value) {
            codeValue.value = value;
            onSubmit();
        }
        function handleRetry() {
            if (__props.retryCount > 0 || __props.retryLoading) {
                return;
            }
            codeValue.value = [];
            errorMessage.value = '';
            emit('retry');
            focusCode();
        }
        function formatTimer(seconds) {
            const minutes = Math.floor(seconds / 60);
            const remainder = seconds % 60;
            return `${minutes}:${remainder.toString().padStart(2, '0')}`;
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", null, [
                _createElementVNode("button", {
                    type: "button",
                    class: _normalizeClass(_ctx.$style.back),
                    onClick: _cache[0] || (_cache[0] = ($event) => (emit('back')))
                }, [
                    _cache[3] || (_cache[3] = _createElementVNode("svg", {
                        width: "20",
                        height: "20",
                        viewBox: "0 0 20 20",
                        fill: "none",
                        xmlns: "http://www.w3.org/2000/svg",
                        "aria-hidden": "true"
                    }, [
                        _createElementVNode("path", {
                            d: "M16.666 9.88095H3.33268M8.33268 14.881L3.33268 9.88095L8.33268 4.88095",
                            stroke: "#aaa9b2",
                            "stroke-width": "1.3",
                            "stroke-linecap": "round",
                            "stroke-linejoin": "round"
                        })
                    ], -1)),
                    _createTextVNode(" " + _toDisplayString(_unref(trlMessage)('login.back')), 1)
                ], 2),
                _createElementVNode("h1", {
                    class: _normalizeClass(_ctx.$style.title)
                }, _toDisplayString(_unref(trlMessage)('login.step.code')), 3),
                _createElementVNode("p", {
                    class: _normalizeClass(_ctx.$style.description)
                }, _toDisplayString(_unref(trlMessage)('login.code.sent', { phone: __props.phone })), 3),
                _createElementVNode("form", {
                    method: "post",
                    onSubmit: _withModifiers(onSubmit, ["prevent"]),
                    class: _normalizeClass(_ctx.$style.form)
                }, [
                    _createElementVNode("div", {
                        ref_key: "pinContainer",
                        ref: pinContainer,
                        class: _normalizeClass(_ctx.$style.codeControl)
                    }, [
                        _createVNode(_unref(PinInputRoot), {
                            modelValue: codeValue.value,
                            "onUpdate:modelValue": [
                                _cache[1] || (_cache[1] = ($event) => ((codeValue).value = $event)),
                                _cache[2] || (_cache[2] = ($event) => (errorMessage.value = ''))
                            ],
                            class: _normalizeClass(_ctx.$style.pinRoot),
                            type: "number",
                            otp: "",
                            disabled: loading.value || nextSendPinSeconds.value > 0,
                            onComplete: onComplete
                        }, {
                            default: _withCtx(() => [
                                (_openBlock(), _createElementBlock(_Fragment, null, _renderList(6, (_, index) => {
                                    return _createVNode(_unref(PinInputInput), {
                                        key: index,
                                        index: index,
                                        class: _normalizeClass([_ctx.$style.codeCell, errorMessage.value && _ctx.$style.codeCellError]),
                                        "aria-label": `${_unref(trlMessage)('code.placeholder')} ${index + 1}`,
                                        "aria-invalid": !!errorMessage.value
                                    }, null, 8, ["index", "class", "aria-label", "aria-invalid"]);
                                }), 64))
                            ]),
                            _: 1
                        }, 8, ["modelValue", "class", "disabled"]),
                        (displayedError.value)
                            ? (_openBlock(), _createBlock(_unref(UiError), {
                                key: 0,
                                class: _normalizeClass(_ctx.$style.error)
                            }, {
                                default: _withCtx(() => [
                                    _createTextVNode(_toDisplayString(displayedError.value), 1)
                                ]),
                                _: 1
                            }, 8, ["class"]))
                            : _createCommentVNode("", true)
                    ], 2),
                    _createVNode(_unref(UiButton), {
                        size: buttonSize.value,
                        "html-type": "submit",
                        fill: "",
                        loading: loading.value,
                        disabled: nextSendPinSeconds.value > 0
                    }, {
                        default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(_unref(trlMessage)('button.confirm')), 1)
                        ]),
                        _: 1
                    }, 8, ["size", "loading", "disabled"])
                ], 34),
                (nextSendPinSeconds.value === 0)
                    ? (_openBlock(), _createElementBlock("button", {
                        key: 0,
                        type: "button",
                        class: _normalizeClass(_ctx.$style.retry),
                        disabled: __props.retryCount > 0 || __props.retryLoading,
                        onClick: handleRetry
                    }, _toDisplayString(retryLabel.value), 11, _hoisted_1))
                    : _createCommentVNode("", true)
            ]));
        };
    }
});
